
import { Component, OnChanges, Input } from '@angular/core';
import { TPriceColumn,
    IPriceValue,
    PriceLineType,
    TPriceLine,
    ITablePrice,
    IPriceLine,
    TPriceValues,
    IPriceLineDetail,
    IOtherLine,
    ITotalLine,
    IHotelLine,
    IAirLine,
    PriceColumnType } from '../../../../ITablePrice';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { ISectorAir, ISectorStay } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { TravellerDataTranslationService } from 'libraries/service/traveller-data-translation/traveller-data-translation.service';
import { TOTAL_KEY } from '../../../../mappers/keys-mapper.service';

import { faAngleUp, faAngleDown, IconDefinition } from "@fortawesome/free-solid-svg-icons";

const HEADER_COLUMN: string = 'lines';

interface ILines {
    value: string | ISectorAir[] | ISectorStay;
    type: PriceLineType;
    data?: TPriceLine;
    isImportant?: boolean;
    additionalInfos?: string[];
}


interface ITableData {
    [key: string]: IPriceValue | ILines | ITableData[];
}

@Component({
    selector: 'price-table',
    template: `
    <div class="table-container">
        <table mat-table
            *ngIf="dataSource"
            [dataSource]="dataSource.values">
            <ng-container *ngFor="let column of dataSource.columns; let first = first;">
                <div [matColumnDef]="column.key" [sticky]="first">
                    <th mat-header-cell *matHeaderCellDef
                        [class.unlimit-first-column-width]="unlimitFirstColumnWidth && first">
                        <ng-container *ngIf="column.traveller; else text">
                            {{ travellerDataTranslationService.getTravellerIdentity(column.traveller) }}
                        </ng-container>

                        <ng-template #text>
                            {{ column.text }}
                        </ng-template>
                    </th>

                    <td mat-cell
                        *matCellDef="let element; let i = index;"
                        [ngSwitch]="element[column.key]?.type"
                        [class.total-cell]="element[column.key]?.type === 'TOTAL'"
                        [class.isImportant]="element[column.key]?.isImportant">
                        <div [class.first-column]="column.key === 'lines'"
                            [class.sectorDetailsIsOpen]="hasSectorDetailsShown">
                            <div *ngSwitchCase="'air'" class="sector-list-container flex">
                                <table-sector-air-summary [sectors]="element[column.key].data.sectors"
                                    (onOpenDetails)="hasSectorDetailsShown = $event">
                                    <btn *ngIf="element[column.key].data?.hasChildren"
                                        class="toggle-button"
                                        classList="no-spacing"
                                        type="actionHeaderLink"
                                        (click)="toggleDetails(element[column.key].data.key)">
                                        <fa-icon [icon]="detailsIsOpen(element[column.key].data.key) ? iconAngleDown : iconAngleUp" class="icon"></fa-icon>
                                    </btn>
                                </table-sector-air-summary>
                            </div>

                            <div *ngSwitchCase="'stay'" class="hotel-list-container flex">
                                <table-sector-stay-summary [sector]="element[column.key].data.hotel"
                                    (onOpenDetails)="hasSectorDetailsShown = $event">
                                    <btn *ngIf="element[column.key].data?.hasChildren"
                                        class="toggle-button"
                                        classList="no-spacing"
                                        type="actionHeaderLink"
                                        (click)="toggleDetails(element[column.key].data.key)">
                                        <fa-icon [icon]="detailsIsOpen(element[column.key].data.key) ? iconAngleDown : iconAngleUp" class="icon"></fa-icon>
                                    </btn>
                                </table-sector-stay-summary>
                            </div>

                            <ng-container *ngSwitchDefault>
                                <div *ngIf="isString(element[column.key]?.value); else isText"
                                    class="sub-line"
                                    [ngClass]="{ total: element[column.key]?.type === 'total' }">
                                    {{ element[column.key]?.quantity ? element[column.key]?.quantity + 'x' : '' }}{{ element[column.key]?.value }}
                                </div>

                                <ng-template #isText>
                                    {{ element[column.key]?.quantity ? element[column.key]?.quantity + 'x' : '' }}
                                    <span [innerHTML]="element[column.key]?.value | priceRealAmount:element[column.key]?.currency"
                                        [ngClass]="{ 'auto-distribute': element[column.key].isAutomaticallyDistributed }">
                                    </span>
                                </ng-template>

                                <additional-infos *ngIf="element[column.key].additionalInfos"
                                    [list]="element[column.key].additionalInfos">
                                </additional-infos>
                            </ng-container>
                        </div>
                    </td>
                </div>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;let last = last;let first = first"
                [hidden]="detailsIsOpen(row.lines.data?.parentKey)"
                [class.last-row]="last"
                [class.first-row]="first"></tr>
        </table>
    </div>
    `,
    styleUrls: ["./price-table.component.scss"]
})

export class PriceTableComponent implements OnChanges{

    @Input()
    public prices: ITablePrice;

    @Input()
    public unlimitFirstColumnWidth: boolean = false;

    public displayedColumns: string[] = [];
    public dataSource: { values: ITableData[]; columns: TPriceColumn[] };
    public labels: ILabels = {
        noPrice: Yii.t('wui-shared-js', 'Aucune information de tarification à afficher'),
    };
    public hasSectorDetailsShown: boolean = false;
    public parentKeySelected: string[] = [];

    public iconAngleUp: IconDefinition = faAngleUp;
    public iconAngleDown: IconDefinition = faAngleDown;

    constructor(
        public readonly travellerDataTranslationService: TravellerDataTranslationService,
    ) {}

    public ngOnChanges(): void {
        if (this.prices) {
            this.dataSource = transformData(this.prices);
            this.displayedColumns = this.dataSource.columns
                .map((column: TPriceColumn) => column.key);
        } else {
            this.dataSource = null;
            this.displayedColumns = [];
        }
    }

    public toggleDetails(key: string): void {
        if (!this.detailsIsOpen(key)) {
            this.parentKeySelected.push(key);
        } else {
            this.parentKeySelected = this.parentKeySelected.filter((k: string) => k !== key);
        }
    }

    public detailsIsOpen(key: string): boolean {
        return this.parentKeySelected.some((k: string) => k === key);
    }

    public isString(value: unknown): boolean {
        return typeof value === 'string' || value instanceof String;
    }
}

function transformData(data: ITablePrice): { values: ITableData[]; columns: TPriceColumn[] } {
    const columns: TPriceColumn[] = [
        {
            key: HEADER_COLUMN,
            type: PriceColumnType.detail,
            text: ''
        },
        ...data.columns
    ];

    const linesValues: ITableData[] = getLineValues(data.lines, data.values, columns);

    return {
        columns,
        values: linesValues
    };
}

function getLineValues(lines: TPriceLine[], values: TPriceValues, columns: TPriceColumn[]): ITableData[] {
    return lines
        .map((line: TPriceLine) => {
            let lineValues: ILines = null;

            switch (line.type) {
                case PriceLineType.air:
                    lineValues = {
                        type: line.type,
                        value: (line as IAirLine).sectors,
                        data: line,
                        isImportant: true
                    };
                    break;
                case PriceLineType.stay:
                    lineValues = {
                        type: line.type,
                        value: (line as IHotelLine).hotel,
                        data: line,
                        isImportant: true
                    };
                    break;
                case PriceLineType.total:
                    lineValues = {
                        type: line.type,
                        value: (line as ITotalLine).text,
                        data: line,
                        isImportant: true,
                        additionalInfos: (line as ITotalLine).additionalInfos,
                    };
                    break;
                default:
                    const textLine: IPriceLineDetail | IOtherLine = line;
                    lineValues = {
                        type: line.type,
                        value: textLine.text,
                        data: textLine,
                    };
            }

            return {
                lines: lineValues,
                ...getValues(line, values, columns),
            };
        });
}

function getValues(line: IPriceLine, values: TPriceValues, columns: TPriceColumn[]): {
    [travellerXId: string]: IPriceValue;
    total?: IPriceValue;
} {
    const lineValues: Map<string, IPriceValue> = values.get(line.key);

    const columnKey: string[] = columns
        .filter((column: TPriceColumn) => column.key !== HEADER_COLUMN)
        .map((column: TPriceColumn) => column.key);

    const constructedValues: { total: IPriceValue } = {
        total: lineValues && lineValues.get(TOTAL_KEY) ? lineValues.get(TOTAL_KEY) : {
            value: '-',
            isAutomaticallyDistributed: false
        } as IPriceValue
    };

    columnKey.forEach((key: string) => {
        constructedValues[key] = lineValues
            && lineValues.get(key)
            && lineValues.get(key).value !== undefined
            ? lineValues.get(key)
            : { value: '-' };
    });

    return constructedValues;
}
